import {
  drawVariant,
  updateExisting,
  colorMap,
  getStructuralVariantsMesh,
} from "./StructuralVariantsBlueprint";
import { structuralVariantsDbs as dbs } from "../../constants/dbs";
import flatten from "../../helpers/flatten";
import StructuralVariant from "../../../../models/StructuralVariant";
import packRegions from "../../helpers/packRegions/packLargeRegions";
import getFilters from "./getFilters";
import ls from "../../../../common/helpers/localStorageActions";

function getDefaultState(filterCategories, maxLimit) {
  const countDict = {};
  const filters = getFilters(filterCategories, maxLimit);

  filters.forEach(({ category, key }) => {
    if (!countDict[category]) countDict[category] = {};
    countDict[category][key] = 0;
  });

  return {
    logLimit: 1,
    largeCount: 0,
    allowCollapse: ls.getPulsarStructCollapse(),
    filters,
    countDict,
    config: {},
    allVariants: [],
    allSorted: [],
    floorHeight: 13,
    variantDict: {},
    floors: [],
    toggleLimit: maxLimit,
    levels: 1,
    g0: 0,
    g1: Infinity,
  };
}

function getStructuralVariantController(track) {
  const pathCat = "Pathogenicity";
  const typeCat = "Type";
  const dbCat = "Structural dbs";
  const maxLimit = 500000;

  let state = getDefaultState({ pathCat, typeCat, dbCat }, maxLimit);
  const structuralVariantsMesh = getStructuralVariantsMesh();

  function onReset() {
    state = getDefaultState({ pathCat, typeCat, dbCat }, maxLimit);
    onHighlightedRegionChanged();
  }

  function onInit() {
    track.setFilters(state.filters);
  }

  function onRangeChange({ chrSegments, start, end }) {
    state.g0 = start;
    state.g1 = end;
    track.loadPosition(chrSegments);
  }

  function hasActiveFilters() {
    const catFilters = state.filters.filter((f) => {
      return (
        (f.value && f.category) || (f.value !== 1 && f.key === "minLength")
      );
    });

    return Boolean(catFilters.length);
  }

  function onRangeStabilized({ start, end }) {
    state.g0 = start;
    state.g1 = end;
    getLargeVisibleCount();

    calcMaxVisibleLevels();
    update();
  }

  function applyFilters() {
    const { allVariants } = state;
    applyFiltersToVariants(allVariants);
    pack(allVariants);
    updateExisting(allVariants, structuralVariantsMesh);
    update();
  }

  function onFiltersChange(updated) {
    const toggleLimit = updated.find((f) => f.key === "maxLength").value;
    state.filters = updated;
    if (toggleLimit !== state.toggleLimit) {
      state.toggleLimit = toggleLimit;
      state.allowCollapse = true;
      ls.setPulsarStructCollapse(state.allowCollapse);
    }
    state.floors = [];
    track.setFilters([...state.filters]);
    setTimeout(() => applyFilters(), 10);
  }

  function sizeSort(a, b) {
    return b.region.size - a.region.size;
  }

  function getLargeVisibleCount() {
    const { allSorted, g0, g1 } = state;
    const hasActive = hasActiveFilters();
    let count = 0;
    let variant = null;

    for (let i = 0; i < allSorted.length; i++) {
      variant = allSorted[i];
      const { size } = variant.region;
      if (variant.region.g1 < g0) continue;
      if (variant.region.g0 > g1) continue;
      if (hasActive && variant.hidden) continue;
      if (size > state.toggleLimit) {
        count += 1;
      }
    }
    state.largeCount = count;
    track.setOverlayProps({ ...state, toggle });
  }

  function onDataReceived({ res, chr }) {
    const vars = processVariants(res, chr);
    if (vars.length === 0) return;
    const allNew = vars.sort(sizeSort);

    state.allSorted = state.allSorted.concat(allNew).sort(sizeSort);
    state.allVariants = state.allVariants.concat(allNew);

    structuralVariantsMesh.setItems(state.allVariants);

    applyFiltersToVariants(allNew);

    drawVariant(allNew, structuralVariantsMesh);

    pack(allNew);

    calcFilterCounts(allNew);
    update();
  }

  function toggle() {
    state.allowCollapse = !state.allowCollapse;
    ls.setPulsarStructCollapse(state.allowCollapse);
    state.floors = [];
    pack(state.allVariants);
  }

  function pack(items) {
    getLargeVisibleCount();

    const hasActive = hasActiveFilters();

    if (state.allowCollapse) {
      packRegions(items, state.floors, 0, hasActive, state.toggleLimit, -2);
    } else {
      packRegions(items, state.floors, 0, hasActive, Infinity, 0);
    }

    updateExisting(state.allVariants, structuralVariantsMesh);

    track.setOverlayProps({ ...state, toggle });

    calcMaxVisibleLevels();

    update();
  }

  function applyFiltersToVariants(items) {
    const pathoDict = state.filters
      .filter((x) => x.category === pathCat && x.value)
      .reduce((a, i) => a.add(i.key), new Set());

    const cnvTypeDict = state.filters
      .filter((x) => x.category === typeCat && x.value)
      .reduce((a, i) => a.add(i.key), new Set());

    const dbDict = state.filters
      .filter((x) => x.category === dbCat && x.value)
      .reduce((a, i) => a.add(i.key), new Set());

    const minFilter = state.filters.find((f) => f.key === "minLength").value;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const outType = cnvTypeDict.size && !cnvTypeDict.has(item.typeIndex);
      const outPatho = pathoDict.size && !pathoDict.has(item.pathoIndex);
      const outDb = dbDict.size && !dbDict.has(item.db.key);
      const outMin = minFilter > 1 && item.region.size < minFilter;

      item.hidden = outDb || outPatho || outType || outMin;
    }
  }

  function processVariants(response, chr) {
    const detailsArr = Object.entries(response)
      .map(([dbName, versions]) => ({ versions, db: dbs[dbName] }))
      .filter(({ db }) => db)
      .map(({ versions, db }) => ({ db, versArr: Object.values(versions) }))
      .map(({ db, versArr }) => ({ db, arr: flatten(versArr) }))
      .map(({ db, arr }) => arr.map((v) => new StructuralVariant(v, chr, db)));

    return flatten(detailsArr).filter(deduplicate);
  }

  function deduplicate(item) {
    if (state.variantDict[item.id]) return false;
    state.variantDict[item.id] = item;
    return true;
  }

  function calcFilterCounts(items) {
    const { countDict, filters } = state;
    for (let i = 0; i < items.length; i++) {
      const x = items[i];
      const dbInd = x.db.key;
      const pathoInd = x.pathoIndex;
      const typeInd = x.typeIndex;

      countDict[dbCat][dbInd] += 1;
      countDict[pathCat][pathoInd] += 1;
      countDict[typeCat][typeInd] += 1;
    }

    filters.forEach((x) => x.setTotalCount(countDict[x.category][x.key]));
    track.setFilters([...filters]);
  }

  function calcMaxVisibleLevels() {
    for (let i = state.floors.length; i > 1; i--) {
      const n = state.floors[i - 1];
      if (!n.check(state.g0, state.g1)) {
        state.levels = i;
        return;
      }
    }
    state.levels = 1;
  }

  function update() {
    const maxLevel = Math.max(0, state.levels);
    structuralVariantsMesh.uniforms.setMinY(-1);
    structuralVariantsMesh.uniforms.setMaxY(maxLevel);
    track.setHeight((1 + maxLevel) * state.floorHeight);
    track.requestRender();
  }

  function onPreferencesChanged() {
    state.config = track.getPreferences();
    updateColorMap();
  }

  function updateColorMap(highlight) {
    const { config } = state;
    const { vectors } = colorMap.calc(config, highlight);
    const map = colorMap.calc(config);

    structuralVariantsMesh.uniforms.setColorMap(vectors);
    state.filters.forEach((x) => {
      x.setColor(map.getKey(x.key)?.color?.hex);
    });
    update();
  }

  function onFilterHighlight(key) {
    updateColorMap(dbs[key] ? key : null);
  }

  function updateSizeLimit(dist) {
    const max = 100000000;
    const min = 100000;
    const logLimit = Math.ceil(Math.log10(dist * 2));
    const lim = Math.ceil(10 ** logLimit);
    const toggleLimit = Math.max(min, Math.min(max, lim));

    if (state.toggleLimit !== toggleLimit) {
      const maxLenFilter = state.filters.find((f) => f.key === "maxLength");

      state.floors = [];
      state.toggleLimit = toggleLimit;
      maxLenFilter.value = toggleLimit;

      track.setFilters(state.filters);
      applyFilters();
      update();
    }
  }

  window.updateSizeLimit = updateSizeLimit;

  function onHighlightedRegionChanged() {
    const [start, end] = track.getHighlightedRegion() || [0, 0];
    updateSizeLimit(end - start);
  }

  return {
    onReset,
    onInit,
    onFiltersChange,
    onHighlightedRegionChanged,
    onRangeChange,
    onRangeStabilized,
    onDataReceived,
    onFilterHighlight,
    onPreferencesChanged,
    meshes: [structuralVariantsMesh],
  };
}

export default getStructuralVariantController;
